import React, { useEffect, useRef } from 'react';

interface Point {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  isTarget?: boolean;
}

export const ConstellationEffect: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const points = useRef<Point[]>([]);
  const mouse = useRef({ x: 0, y: 0, active: false });
  const targets = useRef<{ x: number; y: number }[]>([]);
  const rafIdRef = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initPoints();
    };

    const initPoints = () => {
      const count = Math.floor((canvas.width * canvas.height) / 15000);
      points.current = Array.from({ length: count }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        radius: Math.random() * 1.5 + 0.5,
      }));
    };

    const findTargets = () => {
      const ctaElements = document.querySelectorAll('.cta-target');
      const newTargets: { x: number; y: number }[] = [];
      ctaElements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        newTargets.push({
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        });
      });
      targets.current = newTargets;
    };

    let running = true;
    const animate = () => {
      if (!running) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Update targets periodically or on scroll
      // For simplicity, we'll just use the current targets
      
      points.current.forEach((p, i) => {
        // Move
        p.x += p.vx;
        p.y += p.vy;

        // Bounce
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;

        // Attraction to targets
        targets.current.forEach(t => {
          const dx = t.x - p.x;
          const dy = t.y - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 200) {
            p.vx += dx * 0.0001;
            p.vy += dy * 0.0001;
          }
        });

        // Attraction to mouse
        if (mouse.current.active) {
          const dx = mouse.current.x - p.x;
          const dy = mouse.current.y - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 150) {
            p.vx += dx * 0.0002;
            p.vy += dy * 0.0002;
          }
        }

        // Limit velocity
        const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
        const maxSpeed = 1.5;
        if (speed > maxSpeed) {
          p.vx = (p.vx / speed) * maxSpeed;
          p.vy = (p.vy / speed) * maxSpeed;
        }

        // Draw point
        const isNearTarget = targets.current.some(t => {
          const dx = t.x - p.x;
          const dy = t.y - p.y;
          return Math.sqrt(dx * dx + dy * dy) < 50;
        });

        ctx.beginPath();
        ctx.arc(p.x, p.y, isNearTarget ? p.radius * 2 : p.radius, 0, Math.PI * 2);
        ctx.fillStyle = isNearTarget ? 'rgba(99, 102, 241, 0.9)' : 'rgba(99, 102, 241, 0.6)';
        ctx.fill();
        
        if (isNearTarget) {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.radius * 4, 0, Math.PI * 2);
          ctx.strokeStyle = 'rgba(99, 102, 241, 0.2)';
          ctx.stroke();
        }

        // Draw lines
        for (let j = i + 1; j < points.current.length; j++) {
          const p2 = points.current[j];
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 100) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(99, 102, 241, ${0.4 * (1 - dist / 100)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }

        // Draw line to mouse
        if (mouse.current.active) {
          const dx = p.x - mouse.current.x;
          const dy = p.y - mouse.current.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 150) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(mouse.current.x, mouse.current.y);
            ctx.strokeStyle = `rgba(99, 102, 241, ${0.5 * (1 - dist / 150)})`;
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        }
      });

      rafIdRef.current = requestAnimationFrame(animate);
    };

    const handleMouseMove = (e: MouseEvent) => {
      mouse.current.x = e.clientX;
      mouse.current.y = e.clientY;
      mouse.current.active = true;
    };

    const handleMouseLeave = () => {
      mouse.current.active = false;
    };

    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('scroll', findTargets);
    
    resize();
    findTargets();
    rafIdRef.current = requestAnimationFrame(animate);

    // Periodically refresh targets in case of dynamic content
    const targetInterval = setInterval(findTargets, 2000);

    return () => {
      running = false;
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('scroll', findTargets);
      if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current);
      clearInterval(targetInterval);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0 opacity-100"
      style={{ mixBlendMode: 'normal' }}
    />
  );
};
